import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Plus, Trash2, Star, Upload, Layers, Globe, EyeOff, FileText,
} from 'lucide-react';
import {
  Button, Input, Select, Card, CardContent, Checkbox, KpiCard, Dialog, cn,
} from '../../ui';
import {
  getCursos, getCategorias, getProveedores, deleteCurso, togglePublicadoCurso,
} from '../api';
import { handleAuthError } from '../../../../funciones/authHelper';
import Paginacion from '../../comunes/Paginacion';

// "210 €" sin centavos, símbolo después.
const fmtImporte = (v) =>
  v === null || v === undefined || v === '' ? '—'
    : new Intl.NumberFormat('es-ES', {
        style: 'currency', currency: 'EUR', maximumFractionDigits: 0,
      }).format(Number(v));

const ESTADO_TABS = [
  { id: 'todos',      label: 'Todos' },
  { id: 'activos',    label: 'Activos' },
  { id: 'inactivos',  label: 'Inactivos' },
  { id: 'destacados', label: 'Destacados' },
];

function EstadoPill({ activo, destacado }) {
  if (destacado) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-medium border border-emerald-200">
        <Star className="w-3 h-3 fill-emerald-500 text-emerald-500" />
        Destacado
      </span>
    );
  }
  if (activo) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-medium border border-emerald-200">
        Activo
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-[11px] font-medium border border-slate-200">
      Inactivo
    </span>
  );
}

function ModalidadPill({ value }) {
  const isVideo = value === 'videocurso';
  return (
    <span
      className={cn(
        'inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium',
        isVideo ? 'bg-violet-50 text-violet-700' : 'bg-sky-50 text-sky-700'
      )}
    >
      {isVideo ? 'Videocurso' : 'Curso'}
    </span>
  );
}

export default function LaeCursos() {
  const navigate = useNavigate();
  const [items, setItems]   = useState([]);
  const [cats, setCats]     = useState([]);
  const [provs, setProvs]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ]           = useState('');
  const [fProv, setFProv]   = useState('');
  const [fCat, setFCat]     = useState('');
  const [fMod, setFMod]     = useState('');
  const [estado, setEstado] = useState('todos');
  const [sel, setSel]       = useState(() => new Set());
  const [delItem, setDelItem] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const cargar = async () => {
    setLoading(true);
    try {
      const { data } = await getCursos();   // sin filtros: KPIs y tabla operan en cliente
      setItems(data);
    } catch (e) { if (handleAuthError) handleAuthError(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { cargar(); }, []);
  useEffect(() => {
    getCategorias().then(({ data }) => setCats(data)).catch((e) => handleAuthError && handleAuthError(e));
    getProveedores().then(({ data }) => setProvs(data)).catch(() => { /* sin permiso → selector vacío */ });
  }, []);
  // Resetear a página 1 cuando cambien filtros/búsqueda
  useEffect(() => { setPage(1); }, [q, fProv, fCat, fMod, estado]);

  // KPIs sobre TODO el catálogo (no afectados por filtros)
  const kpis = useMemo(() => ({
    total: items.length,
    activos: items.filter((c) => c.activo).length,
    publicados: items.filter((c) => c.publicado_web).length,
    catalogo: items.filter((c) => c.en_catalogo).length,
  }), [items]);

  const filtrados = useMemo(() => {
    let out = items;
    if (estado === 'activos')    out = out.filter((c) => c.activo);
    if (estado === 'inactivos')  out = out.filter((c) => !c.activo);
    if (estado === 'destacados') out = out.filter((c) => c.destacado);
    if (fProv) out = out.filter((c) => String(c.proveedor_id) === fProv);
    if (fCat)  out = out.filter((c) => (c.categorias || []).some((cc) => String(cc.id) === fCat));
    if (fMod)  out = out.filter((c) => c.modalidad === fMod);
    const t = q.trim().toLowerCase();
    if (t) {
      out = out.filter((c) =>
        (c.nombre || '').toLowerCase().includes(t) ||
        String(c.ref ?? '').toLowerCase().includes(t)
      );
    }
    return out;
  }, [items, estado, fProv, fCat, fMod, q]);

  const totalPaginas = Math.max(1, Math.ceil(filtrados.length / pageSize));
  const pageSafe = Math.min(page, totalPaginas);
  const pageItems = useMemo(
    () => filtrados.slice((pageSafe - 1) * pageSize, pageSafe * pageSize),
    [filtrados, pageSafe, pageSize],
  );

  const toggleSel = (id) => {
    setSel((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelAll = () => {
    setSel((s) => {
      const allIds = filtrados.map((c) => c.id);
      const allSel = allIds.every((id) => s.has(id));
      return allSel ? new Set() : new Set(allIds);
    });
  };
  const allSelected = filtrados.length > 0 && filtrados.every((c) => sel.has(c.id));

  const publicar = async (c) => {
    // Optimistic update; si la llamada falla, revertimos.
    setItems((prev) => prev.map((x) => (x.id === c.id ? { ...x, publicado_web: !c.publicado_web } : x)));
    try { await togglePublicadoCurso(c.id, !c.publicado_web); }
    catch (e) {
      setItems((prev) => prev.map((x) => (x.id === c.id ? { ...x, publicado_web: c.publicado_web } : x)));
      if (handleAuthError) handleAuthError(e);
    }
  };

  const confirmarBorrado = async () => {
    try {
      await deleteCurso(delItem.id);
      setDelItem(null);
      setSel((s) => { const n = new Set(s); n.delete(delItem.id); return n; });
      await cargar();
    } catch (e) { if (handleAuthError) handleAuthError(e); }
  };

  const importarExcel = () => alert('Importación Excel: pendiente de implementar.');

  return (
    <div className="bg-slate-50 text-slate-900 px-6 py-5 min-h-full">

      {/* Header */}
      <div className="mb-4">
        <h1 className="text-xl font-semibold text-slate-900 leading-tight">Cursos</h1>
        <p className="text-sm text-slate-500 mt-1">
          Catálogo de LAE Formación · se publican en <span className="text-slate-700">laeformacion.net</span>
        </p>
      </div>

      {/* KPIs */}
      {/* KpiCard detecta el icono con typeof === 'function', pero lucide-react
          usa forwardRef (typeof === 'object'). Pasamos elementos ya creados con
          las clases de tamaño; el span envoltorio del KpiCard les inyecta el
          color via currentColor. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <KpiCard label="Total cursos" value={kpis.total}      icon={<Layers className="w-3.5 h-3.5" strokeWidth={1.75} />}   iconColor="muted"   subtitle="en LAE Formación" />
        <KpiCard label="Activos"      value={kpis.activos}    icon={<Star className="w-3.5 h-3.5" strokeWidth={1.75} />}     iconColor="warning" subtitle="cursos vivos" />
        <KpiCard label="Publicados"   value={kpis.publicados} icon={<Globe className="w-3.5 h-3.5" strokeWidth={1.75} />}    iconColor="success" subtitle="visibles en web" />
        <KpiCard label="En catálogo"  value={kpis.catalogo}   icon={<FileText className="w-3.5 h-3.5" strokeWidth={1.75} />} iconColor="primary" subtitle="saldrán al PDF" />
      </div>

      {/* Barra de filtros */}
      <Card className="mb-3">
        <CardContent className="p-3">
          {/* Una sola fila en pantallas anchas. Cada Select del DS trae w-full
              hardcoded; lo sobrescribimos con anchos explícitos (twMerge resuelve
              el conflicto). En móvil sigue funcionando via flex-wrap. */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex-1 min-w-[220px]">
              <Input
                prefix={<Search className="w-4 h-4" />}
                placeholder="Buscar curso o referencia…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>

            <Select className="w-44" value={fProv} onChange={(e) => setFProv(e.target.value)}>
              <option value="">Proveedor: todos</option>
              {provs.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
            </Select>

            <Select className="w-44" value={fCat} onChange={(e) => setFCat(e.target.value)}>
              <option value="">Categoría: todas</option>
              {cats.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </Select>

            <Select className="w-40" value={fMod} onChange={(e) => setFMod(e.target.value)}>
              <option value="">Modalidad: todas</option>
              <option value="curso">Curso</option>
              <option value="videocurso">Videocurso</option>
            </Select>

            <div className="inline-flex items-center bg-slate-50 border border-slate-200 rounded-md p-0.5">
              {ESTADO_TABS.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setEstado(t.id)}
                  className={cn(
                    'px-3 py-1.5 text-sm rounded transition-colors',
                    estado === t.id
                      ? 'bg-white text-slate-800 shadow-sm font-medium'
                      : 'text-slate-500 hover:text-slate-700'
                  )}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <Button variant="secondary" onClick={importarExcel}>
              <Upload className="w-4 h-4 mr-1.5" /> Importar Excel
            </Button>
            <Button onClick={() => navigate('/formacion/lae/cursos/nuevo')}>
              <Plus className="w-4 h-4 mr-1.5" /> Crear curso
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabla */}
      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wide text-left">
              <tr>
                <th className="px-3 py-2.5 w-8">
                  <Checkbox checked={allSelected} onChange={toggleSelAll} />
                </th>
                <th className="px-4 py-2.5">Curso</th>
                <th className="px-4 py-2.5 w-20">Ref.</th>
                <th className="px-4 py-2.5">Proveedor</th>
                <th className="px-4 py-2.5">Categoría</th>
                <th className="px-4 py-2.5 w-28">Modalidad</th>
                <th className="px-4 py-2.5 w-16 text-center">Horas</th>
                <th className="px-4 py-2.5 w-24 text-right">Importe</th>
                <th className="px-4 py-2.5 w-32">Estado</th>
                <th className="px-4 py-2.5 w-32">Publicado</th>
                <th className="px-3 py-2.5 w-12 text-center">Cat.</th>
                <th className="px-3 py-2.5 w-8" />
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={12} className="px-4 py-10 text-center text-slate-400">Cargando…</td></tr>
              )}
              {!loading && filtrados.length === 0 && (
                <tr><td colSpan={12} className="px-4 py-10 text-center text-slate-400">
                  {(q || fProv || fCat || fMod || estado !== 'todos')
                    ? 'Sin resultados con esos filtros.'
                    : 'Aún no hay cursos. Crea el primero.'}
                </td></tr>
              )}
              {!loading && pageItems.map((c) => {
                const cat0 = c.categorias && c.categorias[0];
                const more = (c.categorias?.length || 0) - 1;
                return (
                  <tr
                    key={c.id}
                    onClick={() => navigate(`/formacion/lae/cursos/${c.id}`)}
                    className="group border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                  >
                    <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                      <Checkbox checked={sel.has(c.id)} onChange={() => toggleSel(c.id)} />
                    </td>
                    <td className="px-4 py-2.5 font-medium text-slate-800">{c.nombre}</td>
                    <td className="px-4 py-2.5 text-[hsl(var(--brand-light))] font-medium tabular-nums">
                      {c.ref ?? '—'}
                    </td>
                    <td className="px-4 py-2.5 text-slate-600">{c.proveedor?.nombre || '—'}</td>
                    <td className="px-4 py-2.5 text-slate-600">
                      {cat0 ? (
                        <span className="inline-flex items-center gap-1.5">
                          {cat0.nombre}
                          {more > 0 && (
                            <span className="text-[10px] bg-slate-100 text-slate-500 rounded px-1">+{more}</span>
                          )}
                        </span>
                      ) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-4 py-2.5"><ModalidadPill value={c.modalidad} /></td>
                    <td className="px-4 py-2.5 text-center text-slate-600 tabular-nums">{c.horas ?? '—'}</td>
                    <td className="px-4 py-2.5 text-right text-slate-700 tabular-nums">{fmtImporte(c.importe)}</td>
                    <td className="px-4 py-2.5">
                      <EstadoPill activo={c.activo} destacado={c.destacado} />
                    </td>
                    <td className="px-4 py-2.5" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => publicar(c)}
                        disabled={!c.activo}
                        className={cn(
                          'inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-full border transition-colors',
                          !c.activo
                            ? 'bg-slate-50 text-slate-300 border-slate-200 cursor-not-allowed'
                            : c.publicado_web
                              ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                              : 'bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100'
                        )}
                        title={!c.activo
                          ? 'Curso inactivo · activa el curso para poder publicarlo'
                          : c.publicado_web
                            ? 'Publicado en la web · click para despublicar'
                            : 'Borrador · click para publicar'}
                      >
                        {c.publicado_web ? <Globe className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                        {c.publicado_web ? 'Publicado' : 'Borrador'}
                      </button>
                    </td>
                    <td className="px-3 py-2.5 text-center" title={c.en_catalogo ? 'En catálogo' : 'Fuera del catálogo'}>
                      <span
                        className={cn(
                          'inline-block w-2 h-2 rounded-full',
                          c.en_catalogo ? 'bg-[hsl(var(--brand-light))]' : 'bg-slate-200'
                        )}
                      />
                    </td>
                    <td className="px-3 py-2.5 text-right" onClick={(e) => e.stopPropagation()}>
                      <button
                        className="text-slate-300 hover:text-rose-500 opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Eliminar"
                        onClick={() => setDelItem(c)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
        <Paginacion
          page={pageSafe}
          pageSize={pageSize}
          total={filtrados.length}
          onPageChange={setPage}
          onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
        />
      </Card>

      <Dialog
        open={!!delItem}
        onClose={() => setDelItem(null)}
        title="Eliminar curso"
        description={delItem ? `¿Seguro que quieres eliminar "${delItem.nombre}"? Esta acción no se puede deshacer.` : ''}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setDelItem(null)}>Cancelar</Button>
            <Button variant="danger" onClick={confirmarBorrado}>Eliminar</Button>
          </div>
        }
      />
    </div>
  );
}
